import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

const SectionA = ({ assessmentData, setAssessmentData }) => {
  const navigate = useNavigate();
  // Initialize with all questions defaulted to false (No)
  const defaultSectionA = {
    'A1.1': false,
    'A1.2': false,
    'A1.3': false,
    'A1.4': false,
    'A1.5': false
  };
  const [sectionA, setSectionA] = useState(() => {
    const existing = assessmentData.sectionA || {};
    // Merge with defaults to ensure all keys exist
    return { ...defaultSectionA, ...existing };
  });
  // Contact information state
  const [name, setName] = useState(assessmentData.name || '');
  const [email, setEmail] = useState(assessmentData.email || '');
  // Initialize phone with country code extraction
  const initializePhone = () => {
    // Check for new format first (separate fields)
    if (assessmentData.phoneCountryCode && assessmentData.phoneNumber) {
      return { 
        countryCode: assessmentData.phoneCountryCode, 
        number: assessmentData.phoneNumber 
      };
    }
    // Fallback to old format (combined phone)
    if (assessmentData.phone) {
      const phone = assessmentData.phone.trim();
      if (phone.startsWith('+91')) {
        return { countryCode: '+91', number: phone.replace('+91', '') };
      } else if (phone.startsWith('91') && phone.length === 12) {
        return { countryCode: '+91', number: phone.replace('91', '') };
      } else {
        return { countryCode: '+91', number: phone };
      }
    }
    return { countryCode: '+91', number: '' };
  };
  const phoneInit = initializePhone();
  const [phoneCountryCode] = useState(phoneInit.countryCode); // Disabled, so no setter needed
  const [phoneNumber, setPhoneNumber] = useState(phoneInit.number);
  const [companyName, setCompanyName] = useState(assessmentData.companyName || '');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phoneNum) => {
    // Should be 10 digits, not starting with 0
    const phoneRegex = /^[1-9]\d{9}$/;
    return phoneRegex.test(phoneNum);
  };

  // Helper function to get value, defaulting to false (No) if undefined
  const getSectionAValue = (key) => {
    return sectionA[key] !== undefined ? sectionA[key] : false;
  };

  const handleChange = (key, value) => {
    const updated = { ...sectionA, [key]: value };
    setSectionA(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    const newErrors = {};
    
    // Validate email
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Validate phone
    if (!phoneNumber) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(phoneNumber)) {
      newErrors.phone = 'Phone number must be 10 digits and cannot start with 0';
    }
    
    // Set errors and return if validation fails
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    
    // Normalize sectionA: ensure all values are explicitly set (default to false)
    const normalizedSectionA = {
      'A1.1': getSectionAValue('A1.1'),
      'A1.2': getSectionAValue('A1.2'),
      'A1.3': getSectionAValue('A1.3'),
      'A1.4': getSectionAValue('A1.4'),
      'A1.5': getSectionAValue('A1.5')
    };

    // Calculate Basic Risk Score
    let basicScore = 0;
    if (normalizedSectionA['A1.1']) basicScore += 5;
    if (normalizedSectionA['A1.2']) basicScore += 25;
    if (normalizedSectionA['A1.3']) basicScore += 25;
    if (normalizedSectionA['A1.4']) basicScore += 20;
    if (normalizedSectionA['A1.5']) basicScore += 25;
    
    setLoading(true);
    try {
      // Create new lead in Firestore
      let docId = assessmentData.docId;

      if (!docId) {
        const docRef = await addDoc(collection(db, 'assessments'), {
          name,
          email,
          phoneCountryCode,
          phoneNumber,
          phone: `${phoneCountryCode}${phoneNumber}`,
          companyName,
          organizationName: companyName,
          responses: {},
          sectionA: normalizedSectionA,
          sectionABasicScore: basicScore,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        docId = docRef.id;
      }

      setAssessmentData({
        ...assessmentData,
        docId,
        name,
        email,
        phoneCountryCode,
        phoneNumber,
        phone: `${phoneCountryCode}${phoneNumber}`,
        companyName,
        organizationName: companyName,
        sectionA: normalizedSectionA,
        sectionABasicScore: basicScore,
        showSectionB: true // Allows routing to section B
      });

      if (basicScore > 0) {
        setModalMessage(`Based on your responses, your Section A basic risk score is ${basicScore}%. You have qualified for DPDP Act Applicability.`);
      } else {
        setModalMessage('Based on your responses, your Section A basic risk score is 0%. Your organization may not fall under DPDP Act applicability.');
      }
      setShowModal(true);

    } catch (error) {
      console.error('Error submitting assessment:', error);
      setModalMessage('Error submitting assessment. Please try again.');
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontFamily: 'var(--mono)',
    fontSize: '11px',
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    fontWeight: 500
  };
  const requiredStyle = { color: 'var(--accent)', marginLeft: '4px' };
  const helperTextStyle = {
    color: 'var(--muted)',
    fontFamily: 'var(--mono)',
    fontSize: '10px',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    display: 'block',
    marginTop: '0.4rem'
  };

  return (
    <div className="questionnaire-container">
      <div className="section-title-eyebrow">
        <span>Section A</span>
        <span className="dot">◆</span>
        <span>Qualification</span>
      </div>
      <h2 className="section-title">
        Qualification <em>screening.</em>
      </h2>
      <p className="section-description">
        These questions determine whether the business falls under the DPDP Act's purview.
        If you answer YES to any of A1.2–A1.5, you fall under DPDP Act applicability.
      </p>

      <form onSubmit={handleSubmit}>
        {/* Contact Information Section */}
        <div
          className="question-group"
          style={{
            marginBottom: '2rem',
            padding: 'clamp(20px, 2.5vw, 32px)',
            background: 'var(--paper-2)',
            border: '1px solid rgba(26, 10, 34, 0.12)',
            borderTop: '1px solid rgba(26, 10, 34, 0.12)'
          }}
        >
          <label className="question-label" style={{ marginBottom: '1.25rem' }}>Contact Information</label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>
            <div>
              <label htmlFor="name" style={labelStyle}>
                Name <span style={requiredStyle}>*</span>
              </label>
              <input
                type="text"
                id="name"
                className="organization-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Your full name"
              />
            </div>

            <div>
              <label htmlFor="email" style={labelStyle}>
                Email <span style={requiredStyle}>*</span>
              </label>
              <input
                type="email"
                id="email"
                className="organization-input"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors({ ...errors, email: '' });
                  }
                }}
                required
                placeholder="you@company.com"
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem 1.5rem' }}>
            <div>
              <label htmlFor="phone" style={labelStyle}>
                Phone <span style={requiredStyle}>*</span>
              </label>
              <div className="phone-input-container">
                <input
                  type="text"
                  id="phoneCountryCode"
                  className="organization-input phone-country-code"
                  value={phoneCountryCode}
                  disabled
                  readOnly
                  placeholder="+91"
                />
                <input
                  type="tel"
                  id="phone"
                  className="organization-input"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    if (value.length <= 10) {
                      setPhoneNumber(value);
                      if (errors.phone) {
                        setErrors({ ...errors, phone: '' });
                      }
                    }
                  }}
                  required
                  placeholder="10 digit phone number"
                  maxLength="10"
                />
              </div>
              {errors.phone && (
                <span className="error-message">{errors.phone}</span>
              )}
              <small style={helperTextStyle}>
                Enter 10 digits (cannot start with 0)
              </small>
            </div>

            <div>
              <label htmlFor="companyName" style={labelStyle}>
                Company Name <span style={requiredStyle}>*</span>
              </label>
              <input
                type="text"
                id="companyName"
                className="organization-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                placeholder="Your company name"
              />
            </div>
          </div>
        </div>

        <div className="question-group">
          <label className="question-label">A1. Entity & Processing Footprint</label>
          
          <div className="question-item">
            <p className="question-text">
              A1.1. Does your organisation operate within India (registered or functional entity)?
            </p>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.1-yes"
                  name="A1.1"
                  checked={getSectionAValue('A1.1') === true}
                  onChange={() => handleChange('A1.1', true)}
                />
                <label htmlFor="A1.1-yes">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.1-no"
                  name="A1.1"
                  checked={getSectionAValue('A1.1') === false}
                  onChange={() => handleChange('A1.1', false)}
                />
                <label htmlFor="A1.1-no">No</label>
              </div>
            </div>
          </div>

          <div className="question-item">
            <p className="question-text">
              A1.2. Do you process personal data of individuals located in India (customers, employees, contractors)?
            </p>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.2-yes"
                  name="A1.2"
                  checked={getSectionAValue('A1.2') === true}
                  onChange={() => handleChange('A1.2', true)}
                />
                <label htmlFor="A1.2-yes">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.2-no"
                  name="A1.2"
                  checked={getSectionAValue('A1.2') === false}
                  onChange={() => handleChange('A1.2', false)}
                />
                <label htmlFor="A1.2-no">No</label>
              </div>
            </div>
          </div>

          <div className="question-item">
            <p className="question-text">
              A1.3. Do you process personal data digitally or intend to digitise physical records?
            </p>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.3-yes"
                  name="A1.3"
                  checked={getSectionAValue('A1.3') === true}
                  onChange={() => handleChange('A1.3', true)}
                />
                <label htmlFor="A1.3-yes">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.3-no"
                  name="A1.3"
                  checked={getSectionAValue('A1.3') === false}
                  onChange={() => handleChange('A1.3', false)}
                />
                <label htmlFor="A1.3-no">No</label>
              </div>
            </div>
          </div>

          <div className="question-item">
            <p className="question-text">
              A1.4. Do you use third-party IT systems or SaaS tools that store/process personal data?
            </p>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.4-yes"
                  name="A1.4"
                  checked={getSectionAValue('A1.4') === true}
                  onChange={() => handleChange('A1.4', true)}
                />
                <label htmlFor="A1.4-yes">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.4-no"
                  name="A1.4"
                  checked={getSectionAValue('A1.4') === false}
                  onChange={() => handleChange('A1.4', false)}
                />
                <label htmlFor="A1.4-no">No</label>
              </div>
            </div>
          </div>

          <div className="question-item">
            <p className="question-text">
              A1.5. Do you transfer personal data outside India through cloud services or operational workflows?
            </p>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.5-yes"
                  name="A1.5"
                  checked={getSectionAValue('A1.5') === true}
                  onChange={() => handleChange('A1.5', true)}
                />
                <label htmlFor="A1.5-yes">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="A1.5-no"
                  name="A1.5"
                  checked={getSectionAValue('A1.5') === false}
                  onChange={() => handleChange('A1.5', false)}
                />
                <label htmlFor="A1.5-no">No</label>
              </div>
            </div>
          </div>
        </div>

        <div className="alert alert-info">
          <strong>Note:</strong> We calculate a basic DPDP Act applicability score to determine whether your business falls under the DPDP Act's purview. Please answer accurately.
        </div>

        <div className="button-group">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Submitting…' : 'Submit Section A'} <span className="arrow">→</span>
          </button>
        </div>
      </form>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-eyebrow">
                <span>Section A</span>
                <span className="dot">◆</span>
                <span>Outcome</span>
              </div>
              <h2>Assessment <em>outcome.</em></h2>
              <button className="modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{modalMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  closeModal();
                  navigate('/section-b');
                }}
              >
                Start Detailed Assessment <span className="arrow">→</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionA;

